import { useEffect, useState } from 'react';
import { css } from '@emotion/react';
import { Header } from '@frontend/header-navigation';
import { useTranslation } from '@frontend/i18n';
import { Icon } from '@frontend/icons';
import { Page } from '@frontend/page';
import { breakpoints } from '@frontend/responsiveness';
import { useIsSmallMode } from '@frontend/shared';
import { usePreference } from '@frontend/user-preferences';
import { theme } from '@frontend/theme';
import { Button, PopoverMenu, PopoverMenuItem, usePopoverMenu } from '@frontend/design-system';
import { ManageCardButton } from '../components/team-presence/add-card';
import { CardDataLayer, CardView, DemoCardDataLayer } from '../components/team-presence/components';
import { useCardFiltersState, useStoredEntities } from '../components/team-presence/hooks';
import { TourHighlight } from '../components/team-presence/tour-highlight';
import { TourHighlightProvider } from '../components/team-presence/tour-highlight-context';

export const TeamPresence = () => {
  const { t } = useTranslation('calls');
  const { isSmallMode } = useIsSmallMode();
  const [demoDismissed, setDemoDismissedPreference] = usePreference('TeamPresenceDemoDismissed');
  const { entities } = useStoredEntities();
  const hasExistingEntities = entities.length > 0;
  const ActiveDataLayer = demoDismissed || hasExistingEntities ? CardDataLayer : DemoCardDataLayer;
  const [isTourActive, setIsTourActive] = useState(false);
  const [filters, setFilters, defaultFilters] = useCardFiltersState({ persist: Boolean(demoDismissed) });

  useEffect(() => {
    // Save TeamPresenceDemoDismissed as true if there are existing entities
    if (!demoDismissed && hasExistingEntities) {
      setDemoDismissedPreference(true);
      setIsTourActive(false);
      setFilters(defaultFilters);
    }
  }, [demoDismissed, hasExistingEntities, setDemoDismissedPreference, setFilters, defaultFilters]);

  return (
    <TourHighlightProvider isTourActive={isTourActive} setIsTourActive={setIsTourActive}>
      <Page css={styles}>
        <Page.Header>
          <Page.Header.Heading>
            <Page.Header.Title
              title={<Header title={t('Team Status')} />}
              css={{
                // This overrides the default flex direction for the title area
                [`@container (max-width:  ${breakpoints.xsmall.max}px)`]: {
                  flexDirection: 'row',
                  alignItems: 'center',
                  gap: theme.spacing(1),
                },
                justifyContent: isSmallMode ? 'space-between' : undefined,
              }}
            >
              {isSmallMode && <Actions isCompact initialLocationIds={filters.location} />}
            </Page.Header.Title>
            {!isSmallMode && (
              <Page.Header.Action>
                <Actions initialLocationIds={filters.location} />
              </Page.Header.Action>
            )}

            {!isSmallMode && <Page.Header.Subtitle subtitle={t('Monitor your team’s phone activity in real time.')} />}
          </Page.Header.Heading>
        </Page.Header>
        <Page.Body css={{ padding: isSmallMode ? theme.spacing(0, 2, 2, 2) : undefined }}>
          <CardView
            DataLayer={ActiveDataLayer}
            filters={filters}
            setFilters={setFilters}
            defaultFilters={defaultFilters}
            isDemoDismissed={Boolean(demoDismissed)}
            onDemoDismiss={() => setDemoDismissedPreference(true)}
          />
        </Page.Body>
      </Page>
    </TourHighlightProvider>
  );
};

const Actions = ({ isCompact, initialLocationIds }: { isCompact?: boolean; initialLocationIds?: string[] }) => {
  const { t } = useTranslation('calls');

  const { getTriggerProps, getItemProps, getMenuProps } = usePopoverMenu();
  const { entityKey } = useStoredEntities();

  return (
    <>
      <div style={{ display: 'grid', gap: theme.spacing(1), gridAutoFlow: 'column', placeItems: 'center' }}>
        <TourHighlight
          dotId='header-manage'
          message={t('Click here to add or remove Users and Devices from your Team Status dashboard.')}
          placement='bottom'
          dotPosition={{ top: -2, right: -2 }}
          containerCss={{ display: 'inline-flex' }}
          width={240}
        >
          <ManageCardButton
            key={entityKey}
            label={isCompact ? t('Manage') : t('Manage Users and Devices')}
            css={{ textTransform: 'none' }}
            initialLocationIds={initialLocationIds}
          />
        </TourHighlight>
        <Button variant='secondary' iconName='more' {...getTriggerProps()} />
      </div>
      <PopoverMenu {...getMenuProps()}>
        <PopoverMenuItem
          Icon={() => <Icon name='user-voice' />}
          {...getItemProps({
            index: 0,
            onClick: () => {
              window.open(
                'https://feedback.getweave.com/forums/964997-new-weave-app/suggestions/50805257-team-presence-monitor-feedback-monitor-user-activ',
                '_blank'
              );
            },
          })}
        >
          {t('Give feedback')}
        </PopoverMenuItem>
      </PopoverMenu>
    </>
  );
};

const styles = css`
  max-width: 100%;
  overflow: auto;
  height: 100%;

  @container main-content (max-width: ${breakpoints.xsmall.max}px) {
    padding: 0;

    .page-header {
      padding: ${theme.spacing(2, 2, 0, 2)};
      margin: 0;
    }
  }
`;
